<template>
  <section class="spv-form">
    <div class="spv-form__header">
      <div class="spv-form__icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
        </svg>
      </div>
      <h2 class="spv-form__title">SPV Details</h2>
      <button
        type="button"
        class="spv-form__help-link"
        @click="showModal = true"
      >
        What is an SPV?
      </button>
    </div>

    <form class="spv-form__form" @submit.prevent="createSpvRequest">
      <!-- Country & Fee Row -->
      <div class="spv-form__row">
        <div class="spv-form__field">
          <label class="spv-form__label">Country of Incorporation</label>
          <Dropdown
            :required="true"
            class="spv-form__dropdown"
            name="country"
            label="Select Country"
            :validate="true"
            :currentValue="country"
            :options="spvCountries"
            @input-changed="storeValue"
          />
        </div>
        <div class="spv-form__fee-box" v-if="spv.should_manage_spv">
          <span class="spv-form__fee-label">SPV Fees:</span>
          <span class="spv-form__fee-value">USD 30,000</span>
        </div>
      </div>

      <!-- Proposed Names Section -->
      <div class="spv-form__names-section">
        <label class="spv-form__label">Proposed SPV Names</label>
        <p class="spv-form__hint">Based on availability, any of these names will be used.</p>
        
        <div class="spv-form__names-row">
          <InputField
            label="Name of SPV 1"
            class="spv-form__input"
            :currentValue="spv.ProposedName1"
            :validate="false"
            name="ProposedName1"
            @input-changed="storeValue"
          />
          <InputField
            label="Name of SPV 2"
            class="spv-form__input"
            :currentValue="spv.ProposedName2"
            :validate="false"
            name="ProposedName2"
            @input-changed="storeValue"
          />
        </div>
        <div class="spv-form__names-row spv-form__names-row--single">
          <InputField
            label="Name of SPV 3"
            class="spv-form__input"
            :currentValue="spv.ProposedName3"
            :validate="false"
            name="ProposedName3"
            @input-changed="storeValue"
          />
        </div>
      </div>

      <!-- Manage SPV Toggle -->
      <div class="spv-form__toggle-box">
        <div class="spv-form__toggle-header">
          <span class="spv-form__toggle-title">Allow GetEquity manage your SPV</span>
          <ToggleButton
            :value="spv.should_manage_spv"
            @value-selected="setManageStatus"
          />
        </div>
        <p class="spv-form__toggle-desc">
          When turned on, GetEquity will be able to manage your SPV for you.
          This means GetEquity will act as a Corporate Shareholder who holds
          your investors in trust and also help track the SPV shareholders and
          the number of shares owned at a particular time.
        </p>
      </div>

      <!-- Submit Button -->
      <button
        type="submit"
        class="spv-form__submit"
        :disabled="loading"
      >
        <span v-if="loading" class="spv-form__spinner"></span>
        <span v-else>Continue</span>
      </button>
    </form>

    <SpvModal
      v-if="showModal"
      @show-spv-options="showModal = false"
      @close-spv-modal="showModal = false"
    />
  </section>
</template>

<script setup>
import Dropdown from "@/components/general/Dropdown";
import { computed, ref } from "vue";
import SpvModal from "@/components/spv/SpvModal.vue";
import InputField from "@/components/general/InputField.vue";
import ToggleButton from "@/components/general/ToggleButton.vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { getItem, removeItem } from "@/utils/storage";

const store = useStore();
const router = useRouter();

const spv = ref({
  country: null,
  ProposedName1: "",
  ProposedName2: "",
  ProposedName3: "",
  should_manage_spv: false,
  fee: 100,
});

const country = ref("");
const showModal = ref(false);

const spvCountries = computed(() => store.getters["Spv/spvCountries"]);
const loading = computed(() => store.getters["loading"]);

const storeValue = (object) => {
  if (object.value && object.name === "country") {
    country.value = object.value;
    spv.value[object.name] = object.value;
  } else {
    spv.value[object.name] = object.value;
  }
};

const setManageStatus = (value) => {
  spv.value.should_manage_spv = value;
};

const createSpvRequest = async () => {
  const payload = spv.value;
  try {
    const response = await store.dispatch("Spv/requestSpv", payload);
    const id = response.data.data._id;
    const dealId = getItem("dealId");
    if (dealId) {
      const payload = {
        id: dealId,
        deal: {
          spv: id,
        },
      };
      await store.dispatch("Deals/updateDealRequest", payload);
      removeItem("dealId");
    }
    router.push({ name: "SpvBusinessInfo", params: { id } });
  } catch (error) {
    return error;
  }
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$accent-primary: #4D84FF;

.spv-form {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 80px;

  @media (min-width: 768px) {
    padding: 32px;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 32px;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $bg-subtle;
    color: $text-muted;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    color: $text-primary;
    margin: 0;
  }

  &__help-link {
    margin-left: auto;
    font-size: 13px;
    color: $text-muted;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: $accent-primary;
    }
  }

  &__form {
    max-width: 690px;
    margin: 0 auto;
  }

  &__row {
    display: flex;
    gap: 24px;
    margin-bottom: 32px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  &__field {
    flex: 1;
    min-width: 280px;

    @media (max-width: 768px) {
      min-width: 100%;
    }
  }

  &__label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
    margin-bottom: 8px;
  }

  &__dropdown {
    width: 100%;
  }

  &__fee-box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 24px;
    background: $bg-subtle;
    border-radius: 7px;
    height: 48px;
    margin-top: 28px;

    @media (max-width: 768px) {
      margin-top: 0;
    }
  }

  &__fee-label {
    font-size: 14px;
    color: $text-muted;
  }

  &__fee-value {
    font-size: 14px;
    font-weight: 600;
    color: $text-primary;
  }

  &__names-section {
    margin-bottom: 32px;
  }

  &__hint {
    font-size: 13px;
    color: $text-muted;
    margin: 0 0 24px;
  }

  &__names-row {
    display: flex;
    gap: 24px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      flex-direction: column;
    }

    &--single {
      max-width: calc(50% - 12px);

      @media (max-width: 768px) {
        max-width: 100%;
      }
    }
  }

  &__input {
    flex: 1;
  }

  &__toggle-box {
    background: $bg-subtle;
    border-radius: 7px;
    padding: 20px;
    margin-bottom: 32px;
  }

  &__toggle-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
  }

  &__toggle-title {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
  }

  &__toggle-desc {
    font-size: 13px;
    color: $text-muted;
    line-height: 1.5;
    margin: 0;
    max-width: 532px;
  }

  &__submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 32px;
    background: $btn-primary;
    color: white;
    border: none;
    border-radius: 7px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
    min-width: 140px;

    &:hover {
      background: $btn-primary-hover;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
